import {
  CardFooter,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";

const PER_PAGE_OPTIONS = [10, 25, 50, 100, 200];

// Windowed page list: first, last, current +/- 2, with "…" markers in gaps.
const getPageItems = (current, total) => {
  const pages = new Set([1, total]);
  for (let p = current - 2; p <= current + 2; p += 1) {
    if (p >= 1 && p <= total) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const items = [];
  let prev = 0;
  sorted.forEach((p) => {
    if (p - prev > 1) items.push(`ellipsis-${p}`);
    items.push(p);
    prev = p;
  });
  return items;
};

/**
 * TablePagination — unified card-footer pagination for list pages.
 *
 * Props:
 * - meta:       { total, perPage, page } from the API response
 * - page:       current page (controlled by the parent)
 * - setPage:    page setter
 * - perPage:    optional current page size — with setPerPage, renders the
 *               "Show N entries" select (options 10/25/50/100/200)
 * - setPerPage: optional page-size setter (changing it resets to page 1)
 * - className:  extra classes on the <CardFooter>
 */
const TablePagination = ({
  meta = {},
  page,
  setPage,
  perPage,
  setPerPage,
  className = "",
}) => {
  const size = Number(meta.perPage || perPage || 10);
  const totalPages = Math.max(1, Math.ceil(Number(meta.total || 0) / size));
  const current = Number(meta.page || page || 1);

  const goTo = (p) => {
    if (p >= 1 && p <= totalPages && p !== page) setPage(p);
  };

  return (
    <CardFooter
      className={`py-3 d-flex flex-wrap align-items-center justify-content-between ${className}`.trim()}
    >
      {setPerPage ? (
        <div className="d-flex align-items-center">
          <span className="text-sm mr-2">Show</span>
          <Input
            type="select"
            bsSize="sm"
            style={{ width: "auto" }}
            value={perPage || 10}
            aria-label="Entries per page"
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Input>
          <span className="text-sm ml-2">entries</span>
        </div>
      ) : (
        <span />
      )}

      <nav aria-label="Table pagination">
        <Pagination className="pagination mb-0" listClassName="mb-0">
          <PaginationItem disabled={current <= 1}>
            <PaginationLink
              tag="button"
              type="button"
              aria-label="Previous page"
              onClick={() => goTo(current - 1)}
            >
              <i className="fas fa-angle-left" aria-hidden="true" />
              <span className="sr-only">Previous</span>
            </PaginationLink>
          </PaginationItem>

          {getPageItems(current, totalPages).map((item) =>
            typeof item === "number" ? (
              <PaginationItem key={item} active={item === current}>
                <PaginationLink
                  tag="button"
                  type="button"
                  aria-label={`Page ${item}`}
                  aria-current={item === current ? "page" : undefined}
                  onClick={() => goTo(item)}
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            ) : (
              <PaginationItem key={item} disabled>
                <PaginationLink tag="button" type="button" tabIndex={-1}>
                  &hellip;
                </PaginationLink>
              </PaginationItem>
            )
          )}

          <PaginationItem disabled={current >= totalPages}>
            <PaginationLink
              tag="button"
              type="button"
              aria-label="Next page"
              onClick={() => goTo(current + 1)}
            >
              <i className="fas fa-angle-right" aria-hidden="true" />
              <span className="sr-only">Next</span>
            </PaginationLink>
          </PaginationItem>
        </Pagination>
      </nav>
    </CardFooter>
  );
};

export default TablePagination;
